import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import { firstValueFrom } from 'rxjs';
import { AlertNotificationService } from './alert-notification.service';
import { ApiService } from './api.service';
import { BalanceService } from './balance.service';
import { CommonFunctionsService } from './common-functions.service';
import { lineOutstanding, lineTotal, round2 } from './payment-lines.util';
import { getRescheduleDurationDelta } from '../journey/reschedule/omth-rooms';

export interface RescheduleContextLite {
  options: string[];
  rescheduleService: any;
  slot: any;
  priceComparison: Array<{
    item_id: string;
    oldItemId?: string;
    description: string;
    /** item_name on the NEW service's extra — names the gateway line (the old link's data.item is the booked room's). */
    newItemName?: string;
    oldCost: number;
    newCost: number;
    diff: number;
    originalData: any;
  }>;
  priceDifference: number;
  bookedGlobalServiceId: any;
  // A brand-new reschedule fee link to POST (advance- or short-notice). Lives
  // outside priceComparison because it isn't a change to an existing link.
  additionalFee?: {
    item_id: string;
    description: string;
    cost: number;
    data: any;
    /** Gateway charge for the fee when part of it is folded (cost − foldedAmount). */
    chargedCost?: number;
    /** Share of the fee covered by re-attributing overpaid credit. */
    foldedAmount?: number;
  } | null;
  // Whether the customer paid the price difference through the gateway.
  // Drives the per-line data.paid stamps: charged diffs move paid up; the
  // no-payment path leaves paid untouched. Defaults to true when absent so
  // pendingReschedule blobs written before this field existed settle
  // correctly (that path only runs after a confirmed payment).
  paymentTaken?: boolean;
  // Whether the payment settled the whole booking (customer chose to pay the
  // full outstanding balance) or covered only the new charges. Full-settle
  // stamps every link fully paid; new-charges-only moves each compared line's
  // paid up by exactly its charged cost increase. Defaults to true when
  // absent — blobs written before the choice existed were always full-settle.
  settleBalance?: boolean;
  // Fully-paid booking paying only the reschedule fee: the payment covered
  // NOTHING toward the compared links, so their paid carries across unchanged
  // and the cost increases stay outstanding. Only meaningful alongside
  // settleBalance: false. Defaults to false when absent.
  newChargesDeferred?: boolean;
}

export interface RescheduleContext extends RescheduleContextLite {
  booking: any;
}

@Injectable({ providedIn: 'root' })
export class RescheduleService {
  static readonly PENDING_KEY = 'pendingReschedule';

  constructor(
    private apiService: ApiService,
    private balanceService: BalanceService,
    private alerts: AlertNotificationService,
  ) {}

  // RWRSPI-1059: alert the office. Fired here so both the no-payment and
  // post-payment paths are covered, scoped to the new slot so a later
  // reschedule still alerts but a double-clicked submit does not.
  private async alertRescheduled(booking: any, datetime: string): Promise<void> {
    await this.alerts.send(
      'rescheduled',
      { id: booking.id, company_id: booking.company_id },
      `${booking.id}:${datetime}`,
    );
  }

  /**
   * Apply the reschedule against an existing booking: update extra-item links
   * (in place, or replaced for a room change) then update the booking itself.
   * Used by both the no-payment summary path and the post-payment verify path.
   */
  async performUpdates(context: RescheduleContext): Promise<boolean> {
    const {
      booking,
      options,
      rescheduleService,
      slot,
      priceComparison,
      priceDifference,
      bookedGlobalServiceId,
      additionalFee,
    } = context;

    const paymentTaken = context.paymentTaken ?? true;
    const settleBalance = context.settleBalance ?? true;
    const newChargesDeferred = context.newChargesDeferred ?? false;
    const usingNewRoom = options.includes('room') && !!rescheduleService;
    const usingNewResource =
      (options.includes('room') || options.includes('venue')) && !!rescheduleService;

    const globalServiceId = usingNewRoom
      ? rescheduleService.global_service_id
      : bookedGlobalServiceId;

    const newResourceIdRaw = usingNewResource
      ? rescheduleService.resource_id
      : booking._links?.resource?.href?.split('/').pop();
    const newResourceId = newResourceIdRaw != null
      ? Number(newResourceIdRaw)
      : booking.resource_id;

    // The reschedule payment settles the whole booking (outstanding balance
    // + new fees, confirmed July 2026), so on the payment path every link is
    // stamped fully paid: data.paid = cost × quantity at the NEW costs. The
    // no-payment path (nothing was owed) leaves paid untouched — except a
    // folded fee, whose amount moves from the overpaid donor line(s) to the
    // fee link so the total recorded paid still equals the money taken. (On
    // the payment path the settle stamps produce the same result.)
    if (!paymentTaken && (additionalFee?.foldedAmount ?? 0) > 0) {
      let remaining = additionalFee!.foldedAmount!;
      const ordered = [...(priceComparison ?? [])].sort(
        (a, b) =>
          (a.description === 'Room Hire fee' ? 0 : 1) -
          (b.description === 'Room Hire fee' ? 0 : 1),
      );
      for (const row of ordered) {
        if (remaining <= 0) break;
        const paid = Number(row.originalData?.paid) || 0;
        const quantity = Number(row.originalData?.quantity) || 1;
        const headroom = round2(paid - row.newCost * quantity);
        if (headroom <= 0) continue;
        const applied = Math.min(headroom, remaining);
        row.originalData = { ...row.originalData, paid: round2(paid - applied) };
        remaining = round2(remaining - applied);
      }
      if (remaining > 0) {
        console.warn(`Folded fee left £${remaining} unattributed to a donor line`, priceComparison);
      }
    }

    // Reconciled links: a legacy booking (no per-line paid stamps) gets its
    // purchase-history attribution BEFORE any link write. Writing raw legacy
    // data (or only the compared lines) would partially migrate the booking —
    // depositAttributed() would then trust per-line paid and the unwritten
    // lines would read as outstanding on a booking that's actually paid.
    const { links: reconciledLinks, wasLegacy } =
      await this.balanceService.getReconciledLinksDetailed(booking, booking.service_id);

    if (usingNewRoom) {
      await this.replaceLinksForNewRoom(
        booking,
        globalServiceId,
        bookedGlobalServiceId,
        priceComparison,
        paymentTaken,
        settleBalance,
        newChargesDeferred,
        reconciledLinks,
      );
    } else {
      await this.updateLinksInPlace(
        booking,
        globalServiceId,
        priceComparison,
        paymentTaken,
        settleBalance,
        newChargesDeferred,
        reconciledLinks,
        wasLegacy,
      );
    }

    if (additionalFee) {
      await this.createAdditionalFeeLink(
        booking,
        globalServiceId,
        additionalFee,
        paymentTaken,
        settleBalance,
        reconciledLinks,
      );
    }

    try {
      const datetime = moment.utc(slot.start).tz('Europe/London').format();
      const newPrice = booking.price + Math.round(priceDifference * 100);

      // Wood-panelled rooms run 30 minutes longer. When the room changes, adjust
      // the booking's current duration by the wood-panelled delta between the
      // room being left and the one being moved to. booking.duration can be in
      // seconds (list endpoint) or minutes (single-booking GET) depending on
      // where this booking came from — normalise to minutes (RWRSPI-968) rather
      // than a naive `/ 60`, which writes a mangled (or even negative) duration.
      let durationMinutes = CommonFunctionsService.bookingDurationToMinutes(booking.duration);
      if (usingNewRoom) {
        const fromResourceId = booking._links?.resource?.href?.split('/').pop();
        durationMinutes += getRescheduleDurationDelta(fromResourceId, rescheduleService.resource_id);
      }

      const baseBody: any = {
        id: booking.id,
        price: newPrice,
        duration: durationMinutes,
        service_id: usingNewRoom ? globalServiceId + 1 : booking.service_id,
        resource_id: newResourceId,
        datetime,
        notify: true,
        notify_admin: true,
        // The API replaces the settings block wholesale (every other write
        // site spreads for the same reason) — without the spread this wipes
        // extended_at and the booking becomes extendable again.
        settings: {
          ...(booking.settings ?? {}),
          status: 'rescheduled',
          rescheduled_at: new Date().toISOString(),
        },
      };

      // Room change requires a person_id, but we don't know which is valid
      // for the new room/date/time — try each in turn until one succeeds.
      const personIds: number[] = usingNewRoom && Array.isArray(rescheduleService?.person_ids)
        ? rescheduleService.person_ids
        : [];

      let result: any = null;

      if (personIds.length === 0) {
        result = await this.apiService.updateBooking(
          baseBody,
          booking.company_id,
          booking.id,
          false,
        );
        if (result && typeof result !== 'string') {
          await this.alertRescheduled(booking, datetime);
          return true;
        }
      } else {
        for (const personId of personIds) {
          const body = { ...baseBody, person_id: personId };
          result = await this.apiService.updateBooking(
            body,
            booking.company_id,
            booking.id,
            false,
          );
          if (result && typeof result !== 'string') {
            console.log('updateBooking succeeded with person_id', personId);
            await this.alertRescheduled(booking, datetime);
            return true;
          }
          console.warn(`updateBooking failed with person_id ${personId}, trying next`);
        }
      }

      console.error('Update booking did not succeed', result);
      return false;
    } catch (err) {
      console.error('Failed to update booking', err);
      return false;
    }
  }

  private async createAdditionalFeeLink(
    booking: any,
    globalServiceId: any,
    fee: NonNullable<RescheduleContext['additionalFee']>,
    paymentTaken: boolean,
    settleBalance: boolean,
    existingLinks: any[],
  ): Promise<void> {
    const feeCoveredNow = paymentTaken || (fee.foldedAmount ?? 0) > 0;

    // A repeat reschedule means the booking already carries this fee link.
    // POSTing the same item_id again upserts but ACCUMULATES the quantity
    // server-side while overwriting paid with our single-unit stamp (observed:
    // qty 2, paid 25 → a false £25 outstanding), so update the existing link
    // deterministically instead: bump the quantity ourselves and stamp paid
    // for the whole line. Matched by item_id, falling back to the item text
    // for a room change (the old link carries the old service's item_id but
    // was just re-created under fee.item_id by replaceLinksForNewRoom).
    const existing = (existingLinks ?? []).find(
      (l) =>
        String(l?.item_id) === String(fee.item_id) ||
        l?.data?.item === fee.description,
    );
    if (existing) {
      const quantity = (Number(existing.data?.quantity) || 1) + 1;
      const total = round2(fee.cost * quantity);
      const priorPaid = Number(existing.data?.paid) || 0;
      // Full-settle payment: the charge included any prior remainder on this
      // line plus the new fee, so the whole line is paid. Otherwise only this
      // fee's share (the charged fee, folded credit, or nothing) joins the
      // prior stamp.
      const paid = paymentTaken && settleBalance
        ? total
        : Math.min(round2(priorPaid + (feeCoveredNow ? fee.cost : 0)), total);
      try {
        await this.apiService.updateServiceItemExtraLink(
          booking.company_id,
          fee.item_id,
          booking.id,
          {
            global_service_id: globalServiceId,
            item_id: fee.item_id,
            booking_id: `${booking.id}`,
            data: {
              ...(existing.data ?? {}),
              item: fee.description,
              item_id: fee.item_id,
              quantity,
              cost: fee.cost,
              paid,
              note: 'Booking rescheduled',
            },
          },
        );
      } catch (err) {
        console.error(`Failed to update repeat reschedule fee link "${fee.description}"`, err);
      }
      return;
    }

    const data = {
      ...(fee.data ?? {}),
      item: fee.description,
      item_id: fee.item_id,
      quantity: 1,
      cost: fee.cost,
      // The fee is fully covered by the settling payment and/or the folded
      // overpaid credit, so the link is always created 100% paid; only a
      // no-payment reschedule with no fold leaves it outstanding.
      paid: feeCoveredNow ? fee.cost : 0,
      note: 'Booking rescheduled',
    };
    try {
      await this.apiService.linkExtraItem(
        booking.company_id,
        globalServiceId,
        booking.id,
        fee.item_id,
        data,
      );
    } catch (err) {
      console.error(`Failed to create reschedule fee link "${fee.description}"`, err);
    }
  }

  private async updateLinksInPlace(
    booking: any,
    globalServiceId: any,
    priceComparison: RescheduleContext['priceComparison'],
    paymentTaken: boolean,
    settleBalance: boolean,
    newChargesDeferred: boolean,
    reconciledLinks: any[],
    wasLegacy: boolean,
  ): Promise<void> {
    console.log(
      'Updating extra-item links in place with new costs from price comparison',
      priceComparison,
    );
    const comparedItemIds = new Set<string>();
    for (const item of priceComparison) {
      comparedItemIds.add(String(item.item_id));
      const updatedData = { ...(item.originalData ?? {}) };
      updatedData.cost = item.newCost;
      // Full-settle payment: the line is fully paid at its new cost. A
      // new-charges-only payment covered exactly this line's cost increase,
      // so paid moves up by the charged diff (clamped to the new line total —
      // the same clamp the gateway charge applied). A deferred (fee-only)
      // payment covered nothing toward the links: the reconciled paid carries
      // across unchanged (unclamped, preserving any credit) and the increase
      // stays outstanding.
      if (paymentTaken) {
        const fresh = reconciledLinks.find(
          (l) => String(l?.item_id) === String(item.item_id),
        );
        const priorPaid = Number(fresh?.data?.paid ?? updatedData.paid) || 0;
        let intendedPaid: number;
        if (settleBalance) {
          intendedPaid = lineTotal(updatedData);
        } else if (newChargesDeferred) {
          intendedPaid = priorPaid;
        } else {
          const quantity = Number(updatedData.quantity) || 1;
          const chargedDiff = Math.max(round2((item.diff ?? 0) * quantity), 0);
          intendedPaid = Math.min(
            round2(priorPaid + chargedDiff),
            lineTotal(updatedData),
          );
        }
        // Never record LESS than was actually paid. On a downward reschedule the
        // new line total is below what the customer already handed over, and
        // clamping to it erased the difference from the record — the portal then
        // read as settled and no refund was ever surfaced (booking 13904: £825 of
        // room hire lost this way). Credit is preserved as paid > cost × quantity
        // and read back via lineCredit().
        updatedData.paid = Math.max(intendedPaid, priorPaid);
      }

      const payload = {
        global_service_id: globalServiceId,
        item_id: item.item_id,
        booking_id: `${booking.id}`,
        data: updatedData,
      };

      try {
        await this.apiService.updateServiceItemExtraLink(
          booking.company_id,
          item.item_id,
          booking.id,
          payload,
        );
      } catch (err) {
        console.error(`Failed to update extra-item link ${item.item_id}`, err);
      }
    }

    if (paymentTaken && settleBalance) {
      // The settling payment also covered every link the comparison didn't
      // touch (booking fee, certificates, …) — stamp those fully paid too.
      await this.settleRemainingLinks(booking, globalServiceId, comparedItemIds, reconciledLinks);
    } else if (wasLegacy) {
      // No payment (or a new-charges-only payment that left the balance
      // outstanding), but the compared links above were written with their
      // reconciled paid — persist the attribution on the remaining links too,
      // or the booking is left partially migrated and the unwritten lines
      // would read as outstanding.
      await this.persistReconciledPaid(booking, globalServiceId, comparedItemIds, reconciledLinks);
    }
  }

  /** Stamp paid = lineTotal on every booking link not already handled by the caller. */
  private async settleRemainingLinks(
    booking: any,
    globalServiceId: any,
    excludeItemIds: Set<string>,
    links: any[],
  ): Promise<void> {
    for (const link of links ?? []) {
      if (excludeItemIds.has(String(link.item_id))) continue;
      if (lineOutstanding(link.data) <= 0) continue;
      try {
        await this.apiService.updateServiceItemExtraLink(
          booking.company_id,
          link.item_id,
          booking.id,
          {
            global_service_id: link.global_service_id ?? globalServiceId,
            item_id: link.item_id,
            booking_id: `${booking.id}`,
            data: { ...link.data, paid: lineTotal(link.data) },
          },
        );
      } catch (err) {
        console.error(`Failed to settle link ${link.item_id} after payment`, err);
      }
    }
  }

  /** Write the reconciled per-line paid attribution to links the caller didn't already update. */
  private async persistReconciledPaid(
    booking: any,
    globalServiceId: any,
    excludeItemIds: Set<string>,
    links: any[],
  ): Promise<void> {
    for (const link of links ?? []) {
      if (excludeItemIds.has(String(link.item_id))) continue;
      if (link?.data?.paid == null) continue;
      try {
        await this.apiService.updateServiceItemExtraLink(
          booking.company_id,
          link.item_id,
          booking.id,
          {
            global_service_id: link.global_service_id ?? globalServiceId,
            item_id: link.item_id,
            booking_id: `${booking.id}`,
            data: { ...link.data },
          },
        );
      } catch (err) {
        console.error(`Failed to persist reconciled paid on link ${link.item_id}`, err);
      }
    }
  }

  private async replaceLinksForNewRoom(
    booking: any,
    newGlobalServiceId: any,
    bookedGlobalServiceId: any,
    priceComparison: RescheduleContext['priceComparison'],
    paymentTaken: boolean,
    settleBalance: boolean,
    newChargesDeferred: boolean,
    reconciledLinks: any[],
  ): Promise<void> {
    // 1. The caller supplies the RECONCILED existing links (legacy bookings
    //    carry the payment-history attribution) — writing raw legacy data
    //    here would stamp paid: 0 everywhere and flip the booking onto the
    //    per-line model with everything reading unpaid. Also fetch the new
    //    AND booked services' extras; booked extras translate link.item_id ->
    //    item_description so each link matches its new-service equivalent.
    const existingLinks: any[] = reconciledLinks ?? [];
    let newServiceExtras: any[] = [];
    let bookedServiceExtras: any[] = [];

    try {
      newServiceExtras = await firstValueFrom(
        this.apiService.getServiceItemExtras(
          booking.company_id,
          newGlobalServiceId,
          undefined,
          true,
        ),
      );
    } catch (err) {
      console.error('Failed to fetch new service extra items', err);
      return;
    }

    if (bookedGlobalServiceId) {
      try {
        bookedServiceExtras = await firstValueFrom(
          this.apiService.getServiceItemExtras(
            booking.company_id,
            bookedGlobalServiceId,
            undefined,
            true,
          ),
        );
      } catch (err) {
        console.error('Failed to fetch booked service extra items', err);
      }
    }

    // Build a lookup of existing-link item_id -> item_description via booked extras.
    const descriptionByLinkItemId = new Map<string, string>();
    for (const bookedExtra of bookedServiceExtras) {
      if (bookedExtra?.item_id != null && bookedExtra?.item_description) {
        descriptionByLinkItemId.set(String(bookedExtra.item_id), bookedExtra.item_description);
      }
    }

    console.log('Existing links', existingLinks);
    console.log('Booked extras', bookedServiceExtras);
    console.log('New service extras', newServiceExtras);
    console.log('Price comparison', priceComparison);
    console.log('descriptionByLinkItemId', descriptionByLinkItemId);

    // Track which old item_ids actually got replaced with a different new id
    // (only those should be deleted later — a link keeping its id is updated
    // in place by PUT below).
    const oldIdsActuallyChanged = new Set<string>();

    // Item ids already claimed as a rebuild target. Pre-seeded with every
    // existing link's id so no link can upsert into another that's being
    // kept; a link posting to its OWN id stays allowed. Two links landing on
    // one item id merge server-side — quantities accumulate, data comes from
    // the last write, and the earlier link's paid is lost (booking 13883).
    const usedTargetItemIds = new Set<string>(
      existingLinks.map((l: any) => String(l.item_id)),
    );

    // 2. Create a new link for each existing link with the new global_service_id.
    for (const link of existingLinks) {
      const linkId = String(link.item_id);
      const description = descriptionByLinkItemId.get(linkId);

      // item_description is a CATEGORY, not an identity — every drink shares
      // "Drinks". A unique description matches directly; a shared one is
      // disambiguated by catalogue item_name (falling back to the link's own
      // item text). No unique match → the link is carried over unchanged.
      let matchedExtra: any;
      if (description) {
        const candidates = newServiceExtras.filter(
          (ei: any) => ei?.item_description === description,
        );
        if (candidates.length === 1) {
          matchedExtra = candidates[0];
        } else if (candidates.length > 1) {
          const bookedExtra = bookedServiceExtras.find(
            (ei: any) => String(ei?.item_id) === linkId,
          );
          const oldName = bookedExtra?.item_name ?? link.data?.item;
          matchedExtra = candidates.find((ei: any) => ei?.item_name === oldName);
          if (!matchedExtra) {
            console.warn(
              `No unique '${description}' match by name "${oldName}" on new service; keeping link ${linkId} as-is`,
            );
          }
        }
      }
      if (
        matchedExtra &&
        String(matchedExtra.item_id) !== linkId &&
        usedTargetItemIds.has(String(matchedExtra.item_id))
      ) {
        console.warn(
          `Extra "${matchedExtra.item_name}" (${matchedExtra.item_id}) already targeted by another link — keeping link ${linkId} as-is`,
        );
        matchedExtra = undefined;
      }

      let newItemId: any;
      let newData: any;

      console.log('Processing link', link, 'description', description, 'matchedExtra', matchedExtra);

      if (matchedExtra) {
        const newId = String(matchedExtra.item_id);
        usedTargetItemIds.add(newId);
        const pc = priceComparison.find((p) => p.description === description);
        const newCost = pc ? pc.newCost : (link.data?.cost ?? 0);

        // Full-settle payment: every line is fully paid at its new cost. A
        // new-charges-only payment covered exactly this line's cost increase,
        // so paid moves up by the charged diff; a deferred (fee-only) payment
        // covered nothing toward the lines, so the diff share is zero. On the
        // no-payment path the old paid carries across unchanged.
        //
        // These were previously clamped DOWN to the new line total on cost
        // decreases, which silently discarded the customer's credit when a room
        // change made the booking cheaper. Every branch is now floored at the
        // paid value we started with — see the note in updateLinksInPlace.
        const quantity = link.data?.quantity ?? 1;
        const newLineTotal = round2(newCost * (Number(quantity) || 1));
        const priorPaid = Number(link.data?.paid) || 0;
        let intendedPaid: number;
        if (paymentTaken && settleBalance) {
          intendedPaid = newLineTotal;
        } else if (paymentTaken) {
          const chargedDiff = newChargesDeferred
            ? 0
            : Math.max(round2((pc?.diff ?? 0) * (Number(quantity) || 1)), 0);
          intendedPaid = Math.min(round2(priorPaid + chargedDiff), newLineTotal);
        } else {
          intendedPaid = priorPaid;
        }
        const newPaid = Math.max(intendedPaid, priorPaid);

        newItemId = matchedExtra.item_id;
        newData = {
          ...(link.data ?? {}),               // keep questionid etc. — depositAttributed() and the balance maths depend on them
          ...(matchedExtra.data ?? {}),       // pmtTyp etc. from the new service
          item: matchedExtra.item_name,       // new item display name
          item_id: matchedExtra.item_id,      // new item_id
          quantity,                           // keep customer-specific quantity
          cost: newCost,                      // priceComparison cost if matched, else carry over
          deposit: link.data?.deposit,        // keep deposit from the original link
          paid: newPaid,
          note: newId !== linkId
            ? `Booking rescheduled. Updated item id from ${link.item_id}`
            : 'Booking rescheduled',
        };

        if (newId !== linkId) {
          oldIdsActuallyChanged.add(linkId);
        }
      } else {
        // No equivalent on the new service — copy over, settled only when a
        // full-settle payment ran (its charge included this line's remainder).
        if (description) {
          console.warn(`No matching '${description}' on new service; keeping link ${link.item_id} as-is`);
        } else {
          console.warn(`No description for link ${link.item_id}; keeping as-is`);
        }
        newItemId = link.item_id;
        newData = {
          ...(link.data ?? {}),
          // Floored at the prior paid so a settling payment cannot reduce the
          // recorded amount on a line that was already overpaid.
          ...(paymentTaken && settleBalance
            ? {
                paid: Math.max(
                  lineTotal(link.data),
                  Number(link.data?.paid) || 0,
                ),
              }
            : {}),
          note: 'Booking rescheduled',
        };
      }

      // A link landing back on its OWN item id must be PUT, never POSTed. The
      // POST endpoint treats an existing (booking_id, item_id) row as an ADD:
      // it stores `request quantity + stored quantity` (linkItemExtra's switch
      // defaults to that, and its 'replace' case falls through for want of a
      // break, so there is no opt-out). Since the quantity written here is the
      // one just read off the link, every room change DOUBLED it — 1 → 2 → 4 →
      // … ; bookings 13276 and 13314 reached 128. booking.price stayed correct
      // because it is computed from priceDifference, so only the portal's fee
      // breakdown showed the inflation, listing every line twice (13913).
      //
      // Only a genuinely new item id has no row to accumulate onto, so only
      // that case may POST. Accumulation is deliberate for add-on purchases
      // (buying two more bottles should add to the line) — this is why the
      // endpoint behaves that way and why it must not be changed there.
      const targetsOwnId = String(newItemId) === String(link.item_id);
      try {
        if (targetsOwnId) {
          await this.apiService.updateServiceItemExtraLink(
            booking.company_id,
            newItemId,
            booking.id,
            {
              global_service_id: newGlobalServiceId,
              item_id: newItemId,
              booking_id: `${booking.id}`,
              data: newData,
            },
          );
        } else {
          await this.apiService.linkExtraItem(
            booking.company_id,
            newGlobalServiceId,
            booking.id,
            newItemId,
            newData,
          );
        }
      } catch (err) {
        console.error(`Failed to write extra-item link for ${newItemId}`, err);
      }
    }

    // 3. Delete only the original links whose item_id ACTUALLY changed.
    //    Links that kept their id were updated in place above; deleting those
    //    would remove the row we just wrote.
    console.log('Old ids actually changed (to be deleted)', oldIdsActuallyChanged);
    for (const link of existingLinks) {
      const linkId = String(link.item_id);
      if (!oldIdsActuallyChanged.has(linkId)) {
        continue;
      }
      try {
        await this.apiService.deleteServiceItemExtraLink(
          booking.company_id,
          link.item_id,
          booking.id,
        );
      } catch (err) {
        console.error(`Failed to delete old extra-item link ${link.item_id}`, err);
      }
    }
  }
}
